import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { PeriodGranularity, PeriodSubValue, PeriodValue } from "@shared/period-types";

// ─── Exported types (re-exported from shared for consumers that import from here) ──

export type { PeriodGranularity, PeriodSubValue, PeriodValue } from "@shared/period-types";

// ─── Props ────────────────────────────────────────────────────────────────────

interface PeriodSelectorProps {
  periodStart: Date | string; // assessment.periodStart — ISO string or Date
  periodEnd: Date | string;   // assessment.periodEnd   — ISO string or Date
  value: PeriodValue | null;
  onChange: (value: PeriodValue) => void;
  disabled?: boolean;
  className?: string;
}

// ─── Pure helpers (not exported) ─────────────────────────────────────────────

function toUTCDate(d: Date | string): Date {
  return typeof d === "string" ? new Date(d) : d;
}

/** Returns a Date at midnight UTC for the given year / monthIndex (0-based) / day */
function utc(year: number, monthIndex: number, day: number): Date {
  return new Date(Date.UTC(year, monthIndex, day));
}

/** Last day of a month at midnight UTC. Handles leap years automatically. */
function monthEnd(year: number, monthIndex: number): Date {
  return new Date(Date.UTC(year, monthIndex + 1, 0));
}

/** True if [optStart, optEnd] is fully contained within [start, end] */
function fullyContains(start: Date, end: Date, optStart: Date, optEnd: Date): boolean {
  return start <= optStart && end >= optEnd;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

type Month = typeof MONTHS[number];

function getAvailableSemesters(start: Date, end: Date, year: number): Array<"H1" | "H2"> {
  const result: Array<"H1" | "H2"> = [];
  if (fullyContains(start, end, utc(year, 0, 1), utc(year, 5, 30))) result.push("H1");
  if (fullyContains(start, end, utc(year, 6, 1), utc(year, 11, 31))) result.push("H2");
  return result;
}

function getAvailableQuarters(
  start: Date,
  end: Date,
  year: number,
): Array<"Q1" | "Q2" | "Q3" | "Q4"> {
  const defs: Array<["Q1" | "Q2" | "Q3" | "Q4", number, number, number, number]> = [
    ["Q1", 0, 1, 2, 31],
    ["Q2", 3, 1, 5, 30],
    ["Q3", 6, 1, 8, 30],
    ["Q4", 9, 1, 11, 31],
  ];
  return defs
    .filter(([, sm, sd, em, ed]) =>
      fullyContains(start, end, utc(year, sm, sd), utc(year, em, ed)),
    )
    .map(([q]) => q);
}

function getAvailableMonths(start: Date, end: Date, year: number): Month[] {
  return MONTHS.filter((_, i) =>
    fullyContains(start, end, utc(year, i, 1), monthEnd(year, i)),
  );
}

function getAvailableGranularities(
  start: Date,
  end: Date,
  year: number,
): PeriodGranularity[] {
  const result: PeriodGranularity[] = [];
  if (fullyContains(start, end, utc(year, 0, 1), utc(year, 11, 31))) result.push("annually");
  if (getAvailableSemesters(start, end, year).length > 0) result.push("semesterly");
  if (getAvailableQuarters(start, end, year).length > 0) result.push("quarterly");
  if (getAvailableMonths(start, end, year).length > 0) result.push("monthly");
  return result;
}

const GRANULARITY_LABELS: Record<PeriodGranularity, string> = {
  annually: "Annually",
  semesterly: "Semesterly",
  quarterly: "Quarterly",
  monthly: "Monthly",
};

// ─── Component ────────────────────────────────────────────────────────────────

export function PeriodSelector({
  periodStart,
  periodEnd,
  value,
  onChange,
  disabled,
  className,
}: PeriodSelectorProps) {
  const start = toUTCDate(periodStart);
  const end = toUTCDate(periodEnd);
  const year = start.getUTCFullYear();

  // Local state bridges granularity selection and sub-period selection.
  // We update it immediately on user interaction but only call onChange
  // once a complete PeriodValue is formed.
  const [local, setLocal] = useState<{ granularity: PeriodGranularity; period: string } | null>(
    value ? { granularity: value.granularity, period: value.period } : null,
  );

  // Sync from parent when value changes (edit mode, form reset)
  useEffect(() => {
    setLocal(value ? { granularity: value.granularity, period: value.period } : null);
  }, [value]);

  const granularities = getAvailableGranularities(start, end, year);

  const subOptions: string[] = (() => {
    if (!local || local.granularity === "annually") return [];
    if (local.granularity === "semesterly") return getAvailableSemesters(start, end, year);
    if (local.granularity === "quarterly") return getAvailableQuarters(start, end, year);
    return getAvailableMonths(start, end, year);
  })();

  function handleGranularityChange(g: string) {
    const granularity = g as PeriodGranularity;
    if (granularity === "annually") {
      setLocal({ granularity: "annually", period: "" });
      onChange({ granularity: "annually", period: "" });
    } else {
      // Show sub-period select but don't commit until sub-period is chosen
      setLocal({ granularity, period: "" });
    }
  }

  function handleSubPeriodChange(period: string) {
    if (!local || local.granularity === "annually") return;
    setLocal({ granularity: local.granularity, period });
    onChange({ granularity: local.granularity, period: period as PeriodSubValue });
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div>
        <Label className="text-sm font-medium">Period Type</Label>
        <Select
          value={local?.granularity ?? ""}
          onValueChange={handleGranularityChange}
          disabled={disabled}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select period type" />
          </SelectTrigger>
          <SelectContent>
            {granularities.map((g) => (
              <SelectItem key={g} value={g}>
                {GRANULARITY_LABELS[g]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {local && local.granularity !== "annually" && (
        <div>
          <Label className="text-sm font-medium">Period</Label>
          <Select
            value={local.period}
            onValueChange={handleSubPeriodChange}
            disabled={disabled}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              {subOptions.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
